/**
 * §A6's officials popover — the control that replaced the dropdown under every name.
 *
 * A Technical Delegate testing the build said the same thing twice: the names are what he
 * reaches for, and a `<select>` sitting under each one is both ugly and slow to read. The
 * design draft he signed off puts the roster in a popover instead, with a face, a country and
 * one plain reason beside anybody he should think twice about. This is that popover.
 *
 * Three things it must get right, and they are not decoration:
 *
 *  1. **A blocked option is one the API would refuse**, and it is `disabled`. Two roles in
 *     one match and two matches at one kick-off are physically impossible and have no
 *     override at all (§A4 rule 1), so offering them would be offering a refusal.
 *  2. **`Own Country` is offered, struck through.** The design draft disabled it; the spec
 *     does not, and must win — §A7 gives the country rule a deliberate two-step override and
 *     the only place the TD would look for it is the name he wants. Choosing it walks
 *     straight into `Hard-Line Warning · Confirm Override`.
 *  3. **The keyboard reaches everything.** The name that opens this is a real button, so
 *     Enter and Space open it; the arrows walk the list, Home and End jump, Escape closes and
 *     returns focus to the name it came from. A popover a keyboard cannot drive is a control
 *     half the room cannot use.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { TechnicalSlot } from "@hockeytd/shared";

import type { AppointmentFixtureBody, OfficialView } from "../../api/types.js";
import { initialsOf, pickerOptions, type PickerOption } from "./vocabulary.js";

export interface OfficialPickerProps {
  fixture: AppointmentFixtureBody;
  slot: TechnicalSlot;
  officials: readonly OfficialView[];
  /** Every fixture in the fetched scope — the clash and workload advice need the whole day. */
  dayFixtures: readonly AppointmentFixtureBody[];
  /** The name button the popover was opened from, for positioning and for returning focus. */
  anchor: HTMLElement | null;
  /** True where this slot currently carries a rule breach — the header's ⚠ / ✓ mark. */
  breached: boolean;
  onPick: (tmsPersonId: string, option: PickerOption) => void;
  onClose: () => void;
}

/** The popover's own width, so it can be kept on screen. Matches the stylesheet. */
const WIDTH = 326;
const MAX_HEIGHT = 380;

export function OfficialPicker({
  fixture,
  slot,
  officials,
  dayFixtures,
  anchor,
  breached,
  onPick,
  onClose,
}: OfficialPickerProps): React.JSX.Element {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const all = useMemo(
    () => pickerOptions(fixture, slot, officials, dayFixtures),
    [fixture, slot, officials, dayFixtures],
  );

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === "") return all;
    /* The role is searchable too — the TD's reflex on 30 Sep was to type "judge" to find the
       Judges group, and a search that matches only names and countries answered him with an
       empty list, which read as "Judges cannot be picked". */
    return all.filter(
      (option) =>
        option.official.mediaName.toLowerCase().includes(needle) ||
        option.official.country.toLowerCase().includes(needle) ||
        option.official.role.toLowerCase().includes(needle),
    );
  }, [all, search]);

  const listed = shown.filter((option) => !option.isAtd && !option.isJudgeForTo);
  const atds = shown.filter((option) => option.isAtd);
  /* Judges offered for the TO chair (TD request, 26 Sep 2026) — only ever non-empty on a TO
     slot, and drawn below the ATDs. See `PickerOption.isJudgeForTo` for why the heading is
     plain rather than the ATDs' amber "Not Recommended". */
  const judges = shown.filter((option) => option.isJudgeForTo);

  /* Position against the name, and stay on screen. `position: fixed`, so these are viewport
     coordinates — the same arithmetic the design draft does, and the reason the popover is
     never clipped by the pane it was opened from.

     F-20 put the Fixture List in a pane that scrolls inside itself, which gives the anchor a
     second way to move: the page can stand still while the row slides under it. Viewport
     coordinates measured once would then leave the popover hanging over an unrelated row, so
     the measurement is repeated on every scroll — captured, because the pane's own scroll
     event does not bubble to the window — and on every resize. */
  useLayoutEffect(() => {
    if (anchor === null) return;
    function place(): void {
      if (anchor === null) return;
      const box = anchor.getBoundingClientRect();
      const top = Math.max(12, Math.min(box.bottom + 6, window.innerHeight - MAX_HEIGHT));
      const left = Math.max(12, Math.min(box.left, window.innerWidth - WIDTH - 12));
      setPosition({ top, left });
    }
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [anchor]);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  /* Close on a click outside, and on Escape. Escape is handled on the popover itself as well
     as at the document, so it works whether or not focus has stayed inside. */
  useEffect(() => {
    function onDocumentPointerDown(event: MouseEvent): void {
      const box = boxRef.current;
      if (box === null) return;
      const target = event.target;
      if (target instanceof Node && (box.contains(target) || anchor?.contains(target) === true)) {
        return;
      }
      onClose();
    }
    function onDocumentKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDocumentPointerDown);
    document.addEventListener("keydown", onDocumentKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocumentPointerDown);
      document.removeEventListener("keydown", onDocumentKeyDown);
    };
  }, [anchor, onClose]);

  /** Arrow keys walk the options; Home and End jump to the ends. */
  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    const keys = ["ArrowDown", "ArrowUp", "Home", "End"];
    if (!keys.includes(event.key)) return;
    const box = boxRef.current;
    if (box === null) return;
    const buttons = [...box.querySelectorAll<HTMLButtonElement>("button[data-option]")].filter(
      (button) => !button.disabled,
    );
    if (buttons.length === 0) return;
    event.preventDefault();
    const active = document.activeElement;
    const at = buttons.findIndex((button) => button === active);
    const next =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? buttons.length - 1
          : event.key === "ArrowDown"
            ? at + 1 >= buttons.length
              ? 0
              : at + 1
            : at <= 0
              ? buttons.length - 1
              : at - 1;
    buttons[next]?.focus();
  }

  const teams = `${fixture.homeTeamCode ?? "TBC"} v ${fixture.awayTeamCode ?? "TBC"}`;

  return (
    <div
      className="dropdown show"
      role="dialog"
      aria-label={`Choose an official for the ${fixture.displayMatchNo} ${slot} slot`}
      ref={boxRef}
      style={{ top: position.top, left: position.left }}
      onKeyDown={onKeyDown}
      data-testid={`picker-${String(fixture.tmsMatchDbId)}-${slot}`}
    >
      <div className="dropdown__head">
        <span className="dropdown__role">{slot}</span>
        <span className="dropdown__mark" aria-hidden="true">
          {breached ? "⚠" : "✓"}
        </span>
        <span className="dropdown__ctx">
          {fixture.displayMatchNo} · {teams}
        </span>
      </div>

      <div className="dropdown__search">
        <input
          ref={searchRef}
          type="search"
          aria-label="Search officials by name or country"
          placeholder="Search by name or country"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
      </div>

      {shown.length === 0 ? (
        <p className="dropdown__none">
          No official on the roster matches that, and can take the {slot} slot.
        </p>
      ) : null}

      {/* RUN 95 — ONE scroll container for all three groups. Until the Judges group landed,
          the main list scrolled inside its own 268px cap and the ATD group below it just fit;
          three groups no longer do, and the parts outside the cap were only reachable by the
          page's own scroll — which the popover, being position: fixed, does not follow. The TD
          hit exactly that at the venue (26 Sep): the wheel moved the matrix behind the picker
          while the Judges stayed out of reach. The cap and the scrolling now live on this
          wrapper (`.dropdown__scroll`, with `overscroll-behavior: contain` so a wheel at
          either end never chains to the page), and the three lists inside flow naturally. */}
      <div className="dropdown__scroll" data-testid="picker-scroll">
        <ul className="dropdown__list">
          {listed.map((option) => (
            <li key={option.official.tmsPersonId}>
              <PickerOptionButton option={option} onPick={onPick} />
            </li>
          ))}
        </ul>

        {atds.length === 0 ? null : (
          <ul className="dropdown__atds">
            <li className="dropdown__sep">
              <span aria-hidden="true">⚠</span> ATDs — Not Recommended
            </li>
            {atds.map((option) => (
              <li key={option.official.tmsPersonId} className="is-atd">
                <PickerOptionButton option={option} onPick={onPick} />
              </li>
            ))}
          </ul>
        )}

        {judges.length === 0 ? null : (
          <ul className="dropdown__atds dropdown__judges" data-testid="picker-judges">
            <li className="dropdown__sep dropdown__sep--plain">Judges</li>
            {judges.map((option) => (
              <li key={option.official.tmsPersonId} className="is-judge">
                <PickerOptionButton option={option} onPick={onPick} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="dropdown__foot">
        An official from either competing country is shown struck through — the country rule
        is hard-line, and choosing one asks you to confirm an override.
        <span className="photonote">
          Photos come from the officials profiles; initials stand in until they arrive.
        </span>
      </div>
    </div>
  );
}

function PickerOptionButton({
  option,
  onPick,
}: {
  option: PickerOption;
  onPick: (tmsPersonId: string, option: PickerOption) => void;
}): React.JSX.Element {
  const { official, flag } = option;
  const classes = [
    official.gender === null ? "" : `g-${official.gender}`,
    flag?.kind === "own_country" ? "is-own-country" : "",
    flag?.kind === "not_recommended" ? "is-advised" : "",
  ]
    .filter((entry) => entry !== "")
    .join(" ");

  return (
    <button
      type="button"
      data-option={official.tmsPersonId}
      data-testid={`picker-option-${official.tmsPersonId}`}
      className={classes}
      disabled={flag?.blocked === true}
      title={flag?.detail}
      onClick={() => {
        onPick(official.tmsPersonId, option);
      }}
    >
      <span className={`oavatar${official.gender === null ? "" : ` oavatar--${official.gender}`}`} aria-hidden="true">
        {initialsOf(official.mediaName)}
      </span>
      <span className="o-nm">{official.mediaName}</span>
      {flag === null ? (
        <span className="o-nat num">{official.country}</span>
      ) : (
        <span className={`o-flag o-flag--${flag.kind}`}>{flag.label}</span>
      )}
    </button>
  );
}
